import { IPortalClient, PORTAL, PortalClient, PortalEventType, PortalTransport } from '@tencent/slide-sdk-portal';

import { createNanoEvents } from 'nanoevents';


export enum PortClientEventType {
    PortStatusChange = 'portStatusChange',
}

export interface PortClientEventMap {
    [PortClientEventType.PortStatusChange]: (status: PortalStatus) => void;
}

export enum PortalStatus {
    Online = 'online',
    Offline = 'offline',
}

export interface IDetectPortalClient<
    ServiceMap extends Record<string, any> = object,
    ChannelPayloadMap extends Record<string, any> = object,
> extends IPortalClient<ServiceMap, ChannelPayloadMap> {
    isOnline: boolean;

    setGlobalPadId(globalPadId: string): void;
    setCGlobalPadId(cGlobalPadId: string): void;
    setSessionId(sessionId: string): void;
    setType(type: DetectPortalClientType): void;
    setUid(uid: string): void;

    getGlobalPadId(): string;
    getCGlobalPadId(): string;
    getSessionId(): string;
    getType(): DetectPortalClientType;
    getUid(): string;

    close(): void;
}

export enum DetectPortalClientType {
    Local = 'local',
    Drive = 'drive',
    Other = 'other',
}

/**
 * DetectPortalClient 类用于检测门户客户端的在线状态。
 *
 * 该类继承自 PortalClient，并实现了 IDetectPortalClient 接口。它负责管理客户端的在线状态检测和相关操作。
 */
export class DetectPortalClient<
    ServiceMap extends Record<string, any> = object,
    ChannelPayloadMap extends Record<string, any> = object,
> extends PortalClient<ServiceMap, ChannelPayloadMap> implements IDetectPortalClient<ServiceMap, ChannelPayloadMap> {
    // 客户端类型
    private type: DetectPortalClientType;
    // 全局Pad ID
    private globalPadId: string;
    // 云文档全局Pad ID
    private cGlobalPadId: string;
    // 会话ID
    private sessionId: string;
    // 用户id
    private uid: string;

    // 事件管理器
    private event = createNanoEvents<PortClientEventMap>();
    // 客户端状态
    private status: PortalStatus = PortalStatus.Offline;
    // 定时器
    private pingTimer?: NodeJS.Timeout;
    private offlineTimer?: NodeJS.Timeout;

    /**
     * 构造函数
     *
     * @param {PortalTransport} transport - 传输层对象。
     */
    public constructor(transport: PortalTransport) {
        super(transport);
        this.event = createNanoEvents<PortClientEventMap>();
        this.readyPromise = this.initOnlinePromise();
        this.open();
    }

    /**
     * 等待客户端上线
     *
     * 该方法返回一个Promise，当客户端上线时解析。
     */
    public waitForOnline() {
        return this.initOnlinePromise();
    }

    /**
     * 关闭客户端
     *
     * 该方法关闭传输层，并重置定时器。
     */
    public close() {
        this.transport.close();
        this.resetPingTimer();
        this.resetOfflineTimer();
    }

    /**
     * 获取客户端是否在线
     *
     * @returns {boolean} - 如果客户端在线，则返回 true；否则返回 false。
     */
    public get isOnline() {
        return this.status === PortalStatus.Online;
    }

    /**
     * 设置客户端类型
     *
     * @param {DetectPortalClientType} type - 客户端类型。
     */
    public setType(type: DetectPortalClientType) {
        this.type = type;
    }

    /**
     * 获取客户端类型
     *
     * @returns {DetectPortalClientType} - 客户端类型。
     */
    public getType() {
        return this.type;
    }

    /**
     * 设置全局Pad ID
     *
     * @param {string} globalPadId - 全局Pad ID。
     */
    public setGlobalPadId(globalPadId: string) {
        this.globalPadId = globalPadId;
    }

    /**
     * 获取全局Pad ID
     *
     * @returns {string} - 全局Pad ID。
     */
    public getGlobalPadId() {
        return this.globalPadId;
    }

    /**
     * 设置绑定的云文档全局Pad ID
     * @param {string} cGlobalPadId - 云文档全局Pad ID。
     */
    public setCGlobalPadId(cGlobalPadId: string) {
        this.cGlobalPadId = cGlobalPadId;
    }

    /**
     * 获取绑定的云文档全局Pad ID。
     *
     * @returns {string} - 全局Pad ID。
     */
    public getCGlobalPadId() {
        return this.cGlobalPadId;
    }

    /**
     * 设置会话ID
     *
     * @param {string} sessionId - 会话ID。
     */
    public setSessionId(sessionId: string) {
        this.sessionId = sessionId;
    }

    /**
     * 获取会话ID
     *
     * @returns {string} - 会话ID。
     */
    public getSessionId() {
        return this.sessionId;
    }

    /**
     * 设置用户ID
     *
     * 该方法设置实例的用户ID。
     *
     * @param {string} uid - 要设置的用户ID。
     */
    public setUid(uid: string) {
        this.uid = uid;
    }

    /**
     * 获取用户ID
     *
     * 该方法返回实例的用户ID。
     *
     * @returns {string} - 用户ID。
     */
    public getUid() {
        return this.uid;
    }

    /**
     * 打开客户端
     *
     * 该方法打开传输层，并开始检测客户端状态。
     */
    protected open() {
        this.transport.open();
        this.detect();
    }

    /**
     * 检测客户端状态
     *
     * 该方法设置定时器定期发送Ping消息，并根据接收到的Pong消息更新客户端状态。
     */
    private detect() {
        const sendPercentTime = 1000;
        this.pingTimer = setInterval(() => {
            this.transport.send({
                type: PortalEventType.Ping,
                payload: undefined,
                $owner: PORTAL,
            });
            if (!this.offlineTimer) {
                this.offlineTimer = setTimeout(() => {
                    if (this.status === PortalStatus.Offline) return;
                    this.changePortalStatus(PortalStatus.Offline);
                }, sendPercentTime * 3);
            }
        }, sendPercentTime);

        this.transport.receive(PortalEventType.Pong, () => {
            this.resetOfflineTimer();
            if (this.status === PortalStatus.Online) return;
            this.changePortalStatus(PortalStatus.Online);
        });
    }

    /**
     * 重置离线定时器
     *
     * 该方法清除离线定时器。
     */
    private resetOfflineTimer() {
        if (!this.offlineTimer) return;
        clearTimeout(this.offlineTimer);
        this.offlineTimer = undefined;
    }

    /**
     * 重置Ping定时器
     *
     * 该方法清除Ping定时器。
     */
    private resetPingTimer() {
        if (!this.pingTimer) return;
        clearInterval(this.pingTimer);
        this.pingTimer = undefined;
    }

    /**
     * 更改客户端状态
     *
     * 该方法更新客户端状态，并发出状态变化事件。
     *
     * @param {PortalStatus} status - 新的客户端状态。
     */
    private changePortalStatus(status: PortalStatus) {
        this.status = status;
        this.event.emit(PortClientEventType.PortStatusChange, status);
    }

    /**
     * 初始化在线状态Promise
     *
     * 该方法返回一个Promise，当客户端上线时解析。
     *
     * @returns {Promise<void>} - 一个Promise，表示客户端上线。
     */
    private initOnlinePromise() {
        return new Promise<void>((resolve) => {
            if (this.status === PortalStatus.Online) {
                resolve();
                return;
            }
            const off = this.event.on(PortClientEventType.PortStatusChange, (status: PortalStatus) => {
                if (status === PortalStatus.Online) {
                    off();
                    resolve();
                }
            });
        });
    }
}
